import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getStudyProgramPage } from "../../services/StudyProgramPageService";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
} from "@mui/material";

export default function OfferDetailsPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { country_id } = useSelector((state) => state.auth || {});

  const [loading, setLoading] = useState(true);
  const [programData, setProgramData] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);

  // ================= API =================
  useEffect(() => {
    if (!country_id) return;

    const fetchProgramData = async () => {
      setLoading(true);
      try {
        const res = await getStudyProgramPage(country_id);
        if (res?.data?.success && res?.data?.data) {
          setProgramData(res.data.data);
        } else {
          setProgramData(null);
        }
      } catch (err) {
        console.error("❌ Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProgramData();
  }, [country_id]);

  // ================= MATCH SLUG =================
  useEffect(() => {
    if (!slug || !programData?.advantages) return;

    const decodedSlug = decodeURIComponent(slug);

    const matched = programData.advantages.find(
      (item) => item.key.toLowerCase() === decodedSlug.toLowerCase()
    );

    setSelectedOffer(matched || null);
  }, [slug, programData]);

  // ================= CLEAN HTML =================
  const cleanHTML = (html) => {
    if (!html) return "";

    return html
      // ❌ remove styles & spans
      // .replace(/style="[^"]*"/gi, "")
      .replace(
        /<(?!ol\b|ul\b)([a-z][a-z0-9]*)\b([^>]*)\sstyle="[^"]*"([^>]*)>/gi,
        "<$1$2$3>"
      )
      .replace(/<span[^>]*>/gi, "")
      .replace(/<\/span>/gi, "")

      // ❌ remove &nbsp
      .replace(/&nbsp;/gi, " ")

      // ❌ remove extra <br>
      .replace(/<br\s*\/?>/gi, "")

      // ✅ convert standalone strong (titles) → h3
      .replace(
        /<p>\s*<strong>(.*?)<\/strong>\s*<\/p>/gi,
        "<h3>$1</h3>"
      )

      // ✅ convert inline strong headings (your main issue)
      .replace(
        /(<br\s*\/?>)?\s*<strong>(.*?)<\/strong>\s*(<br\s*\/?>)?/gi,
        "</p><h3>$2</h3><p>"
      )

      // ❌ remove empty paragraphs
      .replace(/<p>\s*<\/p>/gi, "")

      // ❌ remove multiple closing/opening p issues
      .replace(/<\/p>\s*<p>/gi, "</p><p>")

      // ❌ trim
      .trim();
  };

  // ✅ CONTENT STYLING (KEY PART)
  const contentStyles = {
    fontSize: { xs: 14, sm: 15, md: 16 },
    lineHeight: 1.625,
    textAlign: "justify",

    "& p": {
      marginBlockStart: '0px !important',
      marginBottom: "16px",
    },

    "& p:last-of-type": {
      marginBottom: "0px",
    },

    // ✅ TARGET KEY-VALUE LINES (Language, Currency...)
    "& p:has(strong)": {
      position: "relative",
      paddingLeft: "20px",
      marginBottom: "8px",
      fontWeight: 600,
      fontSize: "18px"
    },

    // ✅ ADD ICON BEFORE THEM
    "& p:has(strong)::before": {
      content: '"\\f101"',
      fontFamily: "FontAwesome",
      position: "absolute",
      left: 0,
      top: "4px",
      color: "#fd0000",
      fontSize: "14px",
    },

    "& h3": {
      fontSize: "20px",
      fontWeight: 700,
      marginTop: "20px",
      marginBottom: "10px",
      color: "#000",
    },

    // ✅ REMOVE DEFAULT BULLETS
    "& ul": {
      listStyle: "none",
      paddingLeft: "0px",
      marginBottom: "12px",
    },

    // ✅ CUSTOM LIST ITEM
    "& li": {
      position: "relative",
      paddingLeft: "28px",
      marginBottom: "4px",
      lineHeight: 1.7,
    },

    "& li p": {
      marginBottom: "6px !important",
      marginTop: "0px !important",
    },

    // ✅ FIRST ITEM
    "& li:first-of-type": {
      marginTop: "-10px",
    },

    // ✅ LAST ITEM
    "& li:last-of-type": {
      marginBottom: "15px",
    },

    // ✅ ADD ARROW ICON
    "& li::before": {
      content: '"\\f101"',
      fontFamily: "FontAwesome",
      position: "absolute",
      left: '5px',
      top: "2px",
      fontSize: "14px",
      color: "#fd0000",
    },

  };


  // ================= UI =================
  if (loading) return <div>Loading...</div>;
  if (!selectedOffer) return <div>No Data Found</div>;

  return (
    <>
      {/* ---------- HEADER ---------- */}
      <Box
        sx={{
          background: "linear-gradient(90deg, #053770 0%, #002045 100%)",
          color: "#fff",
          py: { xs: 3, md: 5 },
        }}
      >
        <Container maxWidth="lg">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/study-programs")}
            sx={{ mb: 2, color: "#fff" }}
          >
            Back
          </Button>

          <Typography
            sx={{
              fontSize: { xs: 22, md: 30 },
              fontWeight: 700,
            }}
          >
            {selectedOffer.key}
          </Typography>
        </Container>
      </Box>

      {/* ---------- CONTENT ---------- */}
      <Container
        maxWidth="lg"
        sx={{
          py: { xs: 3, md: 5 },
        }}
      >
        <Box
          sx={contentStyles}
          dangerouslySetInnerHTML={{
            __html: cleanHTML(selectedOffer.value),
          }}
        />
      </Container>
    </>
  );
}